/* eslint-disable max-lines */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Button,
  IconButton,
  CreditsCounter,
  AddIcon,
  Tag,
  Tooltip,
  DropdownButton,
  MiniLoader,
  NoPrint,
} from '@dataplace.ai/ui-components/atoms'
import { MiniMapTile } from '@dataplace.ai/ui-components/atoms/MiniMapTile/MiniMapTile'
import { useAppDispatch } from 'apps/placeme/src/redux/hooks'
import { resetLocationState, saveComparedAnalyseId } from 'apps/placeme/src/features/ChooseLocationReport/chooseLocationSlice'
import { AuthContext } from '@dataplace.ai/features/components/AuthContext'
import { LanguageSelector, MainDropdown, StatusInfoLabel } from '@dataplace.ai/ui-components/molecules'
import { placemePlans } from '@dataplace.ai/constants'
import { useFeatureAccess, useSaveLocation } from '@dataplace.ai/functions/hooks'
import useRedirectBasedOnProjectState from 'apps/placeme/src/customHooks/useRedirectBasedOnProjectState'
import { createFlashMessage, getAxios } from '@dataplace.ai/functions/utils'
import { dataAttributes, featureAccessIds } from 'apps/placeme/src/constants/constants'
import styled, { css } from 'styled-components'
import { IDropdownButtonProps } from '@dataplace.ai/ui-components/atoms/Buttons/DropdownButton/@types/IDropdownButton.props'
import { useOverviewData } from '@placeme/components/overview/hooks/useOverviewData'
import { ReactComponent as Download } from '../../../../../../../../libs/shared/assets/src/lib/icons/dataplace/download.svg'
import { ReactComponent as TrashIcon } from '../../../../../../../../libs/shared/assets/src/lib/icons/trashIcon.svg'
import { RootState } from '../../../../../redux/store'
import { ExitFromAnalysePopup, SaveAnalyseModal } from '../../molecules'
import {
  resetAnalysisState, saveComparedLocation, toggleIsEdit,
} from '../../../slice/analysisSlice'
import { tilesPdfList } from '../../../utils/tilesPdfList'
import { AddAnotherLocationModal } from '../../molecules/AddAnotherLocationModal'
import { exitFromAnalyseAction } from '../../../functions/exitFromAnalyse'
import {
  ActionsWrapper, AddAnotherLocation, CreditsCounterWrapper, LocationWrapper, StyledBlueSpan,
  StyledDropdownButton, Wrapper, DropdownContentWrapper, StyledDropdownHeader,
} from './AnalysePageHeader.styles'
import DownloadButtons from './components/DownloadButtons'

const DropdownMainButtonWrapper = styled.span(({
  theme: {
    palette, typography,
  },
}) => css`
  color: ${palette.light.white};
  font-size: ${typography.small.pt_13_semibold.fontSize};
  font-weight: ${typography.small.pt_13_semibold.fontWeight};
  padding: 10px 10px 10px 20px;
`)

export const AnalysePageHeader = (): JSX.Element => {
  // constants
  const {
    analysisDbInfo, creditsAmount, canBeSave, values, plans, comparedLocation, comparisonState,
    synchronousTilesLoading, isEdit,
  } = useSelector((state: RootState) => state.analysis)
  const {
    value, currentSubscriptionData, analyseId, comparedAnalyseId, userType,
  } = useSelector((state: RootState) => state.location)
  const address = value?.address || ''
  const navigationRef = useRef<HTMLDivElement>(null)
  const counterRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const redirectBasedOnProjectState = useRedirectBasedOnProjectState()
  const { hasFeatureAccess } = useFeatureAccess()
  const hasKanbanAccess = hasFeatureAccess(featureAccessIds.kanban)
  const hasExcelAccess = hasFeatureAccess(featureAccessIds.excelReport)
  const { saveLocation } = useSaveLocation()
  const { dataLoaded: overviewDataLoaded } = useOverviewData()

  // state
  const [isExcelLoading, setIsExcelLoading] = useState(false)
  const [compareLocationLoading, setCompareLocationLoading] = useState(false)
  const [compareLocationStatusLabel, setCompareLocationStatusLabel] = useState(false)
  const [errorCompareLocation, setErrorCompareLocation] = useState(false)
  const [errorTilesList, setErrorTilesList] = useState<string[]>()
  const [isLocationSaving, setIsLocationSaving] = useState(false)
  const [locationSaved, setLocationSaved] = useState(false)

  const authContext = useContext(AuthContext)

  const canSaveLocation = useMemo(() => (
    !locationSaved && hasKanbanAccess && !analysisDbInfo?.locationExists && !analysisDbInfo?.locationId
  ), [hasKanbanAccess, analysisDbInfo?.locationExists, analysisDbInfo?.locationId, locationSaved, userType])

  const isTrial = useMemo(() => (
    [placemePlans.trial, placemePlans.white, undefined].includes(currentSubscriptionData?.value?.planExact)
  ), [currentSubscriptionData?.value?.planExact])

  const onSaveLocation = useCallback(async () => {
    if (!analysisDbInfo?.address || !analysisDbInfo.locationLat || !analysisDbInfo.locationLng) return
    setIsLocationSaving(true)
    await saveLocation({
      address: analysisDbInfo.address,
      lat: analysisDbInfo.locationLat,
      lng: analysisDbInfo.locationLng,
    })
    setIsLocationSaving(false)
    setLocationSaved(true)
    createFlashMessage({
      message: 'status.success.generic.ok',
    })
  }, [analysisDbInfo, getAxios])

  const dropdownContent = useMemo(() => {
    const content: IDropdownButtonProps['dropdownContent'] = []
    if (canSaveLocation) {
      content.push({
        id: 'saveLocation',
        button: (
          <StyledDropdownButton>
            {t('placeme.analysis_navbar.dropdown.save_location')}
            {isLocationSaving ? <MiniLoader /> : null}
          </StyledDropdownButton>
        ),
        disabled: isLocationSaving,
        onClick: onSaveLocation,
      })
    }
    return content
  }, [canSaveLocation, isLocationSaving])

  // we need ref to get current state in event listener
  const [token, _setToken] = useState('')
  const tokenRef = React.useRef(token)
  const setToken = (newTokenRef: string) => {
    tokenRef.current = newTokenRef
    _setToken(newTokenRef)
  }
  // functions
  const checkIfAnyTileSave = () => {
    let isAnyTileSave = false
    if (overviewDataLoaded) return true
    if (values) {
      values.forEach(cat => {
        cat.tiles.forEach(tile => {
          if (tile.data?.value) {
            isAnyTileSave = true
          }
          return isAnyTileSave
        })
      })
    }
    return isAnyTileSave
  }

  const checkIfCanBeInPDF = () => {
    if (overviewDataLoaded) return true
    let isAnyTileSave = false
    if (values) {
      values.forEach(cat => {
        cat.tiles.forEach(tile => {
          if (tile.data?.value && tilesPdfList.some(key => new RegExp(`${key}.*`, 'g').test(tile.id))) {
            isAnyTileSave = true
          }
          return isAnyTileSave
        })
      })
    }
    return isAnyTileSave
  }

  const checkIfCanBeInExcel = () => {
    if (isTrial) {
      return false
    }
    let isAnyTileSave = false
    if (values) {
      values.forEach(cat => {
        cat.tiles.forEach(tile => {
          if (tile.data?.value && tilesPdfList.some(key => new RegExp(`${key}.*`, 'g').test(tile.id))) {
            isAnyTileSave = true
          }
          return isAnyTileSave
        })
      })
    }
    return isAnyTileSave
  }

  const pdfDownloadDisabled = useMemo(() => (
    synchronousTilesLoading || !currentSubscriptionData?.value || compareLocationLoading || !checkIfCanBeInPDF()
  ), [synchronousTilesLoading, currentSubscriptionData?.value, compareLocationLoading, checkIfCanBeInPDF])

  const excelDownloadDisabled = useMemo(() => (
    synchronousTilesLoading || !currentSubscriptionData?.value || compareLocationLoading || !checkIfCanBeInExcel()
  ), [synchronousTilesLoading, currentSubscriptionData?.value, compareLocationLoading, checkIfCanBeInExcel])

  const handleDelete = () => {}

  const handleDeleteCompared = () => {
    window.localStorage.removeItem('comparedLocation')
    window.localStorage.removeItem('comparedAnalyseId')
    dispatch(saveComparedLocation(undefined))
    dispatch(saveComparedAnalyseId(undefined))
  }

  const getMaxNumber = () => {
    if (typeof currentSubscriptionData?.value?.credits === 'number'
      && typeof currentSubscriptionData?.value?.usage === 'number') {
      return currentSubscriptionData?.value?.usage + currentSubscriptionData?.value?.credits
    }
    return currentSubscriptionData?.value?.credits
  }

  // hooks
  useEffect(() => {
    // fetches user token id from authContext
    authContext.userData?.user?.getIdToken()?.then(response => {
      setToken(response)
    })
  }, [authContext])

  useEffect(() => {
    let prevScrollPos = window.pageYOffset
    window.onscroll = function () {
      const currentScrollPos = window.pageYOffset
      if (
        currentScrollPos > 100
        && navigationRef.current
        && counterRef.current
      ) {
        if (prevScrollPos > currentScrollPos) {
          navigationRef.current.style.top = '0'
          navigationRef.current.style.opacity = '1'
          navigationRef.current.style.zIndex = '700'
          counterRef.current.style.top = '0'
          counterRef.current.style.opacity = '1'
          counterRef.current.style.zIndex = '701'
        } else {
          navigationRef.current.style.top = '-8rem'
          navigationRef.current.style.opacity = '0'
          counterRef.current.style.top = '-8rem'
          counterRef.current.style.opacity = '0'
        }
      }
      prevScrollPos = currentScrollPos
    }
    if (prevScrollPos === 0
      && navigationRef.current
      && counterRef.current) {
      navigationRef.current.style.top = '0'
      navigationRef.current.style.opacity = '1'
      navigationRef.current.style.zIndex = '700'
      counterRef.current.style.top = '0'
      counterRef.current.style.opacity = '1'
      counterRef.current.style.zIndex = '701'
    }
  }, [])

  useEffect(() => {
    if (counterRef.current) {
      counterRef.current.style.top = '0'
      counterRef.current.style.opacity = '1'
      counterRef.current.style.zIndex = '701'
    }
  }, [creditsAmount])

  useEffect(() => {
    // handle loading
    if (comparisonState?.filter(item => item?.loading === true).length) {
      setCompareLocationLoading(true)
    } else {
      setCompareLocationLoading(false)
    }
    // handle error
    const errorTiles = comparisonState?.filter(item => item?.error?.length)
    if (errorTiles?.length) {
      setErrorCompareLocation(true)
      const errorTilesLabels: string[] = []

      errorTiles?.forEach(tile => values?.forEach(cat => {
        const tileLabel = cat?.tiles?.find(t =>
          t?.id === tile?.tile)?.label as string
        if (tileLabel) {
          errorTilesLabels?.push(tileLabel)
        }
      }))
      setErrorTilesList(errorTilesLabels)
    }
  }, [comparisonState])

  return (
    <>
      {compareLocationStatusLabel && (
        <StatusInfoLabel
          error={errorCompareLocation}
          errorParagraph={t('placeme.compare_location.error_para')}
          errorSpan={`${t('placeme.compare_location.error_span')}: ${errorTilesList?.map((item, index) =>
            ` ${t(item)}${index === errorTilesList?.length - 1 ? '.' : ''}`)}`}
          errorSpan2={t('placeme.compared_location.error_span2')}
          handleClose={() => {
            setErrorCompareLocation(false)
            setCompareLocationStatusLabel(false)
          }}
          loading={compareLocationLoading}
          loadingParagraph={t('placeme.compare_location.loading_para')}
          loadingSpan={t('placeme.compare_location.loading_span')}
          successParagraph={t('placeme.compare_location.success_para')}
          successSpan={t('placeme.compare_location.success_span')}
        />
      )}
      <NoPrint>
        <CreditsCounterWrapper ref={counterRef}>
          <CreditsCounter
            currentPlan={currentSubscriptionData?.value?.planExact || ''}
            maxNumber={getMaxNumber()}
            number={currentSubscriptionData?.value?.credits || 0}
            plans={plans}
            subscriptionData={currentSubscriptionData?.value}
          />
        </CreditsCounterWrapper>
        <Wrapper ref={navigationRef}>
          <MiniMapTile
            comparedLocation={comparedLocation?.location}
            location={value}
            mapId='currentLocation'
            shouldMapStyleBeSave
          />
          <LocationWrapper>
            <span>{t('placeme.page_header.location_analysis')}</span>
            <div>
              <span>{address}</span>
              {checkIfAnyTileSave()
                ? (
                  <ExitFromAnalysePopup
                    handleDelete={() => {
                      const isProjectSaved = analysisDbInfo?.visible
                      dispatch(resetAnalysisState())
                      dispatch(resetLocationState())
                      handleDeleteCompared()
                      redirectBasedOnProjectState(isProjectSaved)
                    }}
                    trigger={
                      !analysisDbInfo?.visible
                        ? (
                          <IconButton
                            iconAlt='edit'
                            iconSrc='assets/icons/dataplace/edit.svg'
                          />
                        )
                        : <></>
                    }
                  />
                )
                : canBeSave && (
                  <IconButton
                    iconAlt='edit'
                    iconSrc='assets/icons/dataplace/edit.svg'
                    onClick={() => {
                      const isProjectSaved = analysisDbInfo?.visible
                      dispatch(resetAnalysisState())
                      dispatch(resetLocationState())
                      handleDeleteCompared()
                      redirectBasedOnProjectState(isProjectSaved)
                    }}
                  />
                )}
            </div>
            {!comparedLocation?.location
              ? (canBeSave
                ? (
                  <AddAnotherLocationModal
                    setCompareLocationStatusLabel={setCompareLocationStatusLabel}
                    trigger={(() => {
                      const button = (
                        <AddAnotherLocation disabled={isTrial}>
                          <AddIcon />
                          <span>{t('placeme.compare_with_another_location')}</span>
                        </AddAnotherLocation>
                      )
                      return isTrial
                        ? (
                          <Tooltip
                            content={t('placeme.compare_with_another_location.disabled_for_trial')}
                            position='right top'
                          >
                            {button}
                          </Tooltip>
                        )
                        : button
                    })()}
                  />
                )
                : (() => {
                  const button = (
                    <AddAnotherLocation disabled={isTrial || !canBeSave}>
                      <AddIcon />
                      <span>{t('placeme.compare_with_another_location')}</span>
                    </AddAnotherLocation>
                  )
                  return isTrial
                    ? (
                      <Tooltip
                        content={t('placeme.compare_with_another_location.disabled_for_trial')}
                        position='right top'
                      >
                        {button}
                      </Tooltip>
                    )
                    : button
                })())
              : (!comparedAnalyseId
                ? (
                  <AddAnotherLocation>
                    <span>{t('placeme.compared_location.comparing_with')}</span>
                    <Tag
                      color='#EDF0F5'
                      margin='0 5px'
                    >
                      <span>{comparedLocation?.location?.address}</span>
                    </Tag>
                    <AddAnotherLocationModal
                      setCompareLocationStatusLabel={setCompareLocationStatusLabel}
                      trigger={(
                        <IconButton
                          iconAlt='edit'
                          iconSrc='assets/icons/dataplace/edit.svg'
                        />
                      )}
                    />
                    <TrashIcon
                      className='trash-icon'
                      onClick={handleDeleteCompared}
                    />
                  </AddAnotherLocation>
                )
                : (
                  <AddAnotherLocation>
                    <span>{t('placeme.compared_location.comparing_with')}</span>
                    <Tag
                      color='#EDF0F5'
                      margin='0 0 0 5px'
                    >
                      <span>{comparedLocation?.location?.address}</span>

                    </Tag>
                  </AddAnotherLocation>
                )
              )}

          </LocationWrapper>
          <ActionsWrapper data-cy={dataAttributes.analysePage.header.actionsWrapper}>
            <MainDropdown
              content={(
                <DropdownContentWrapper>
                  <DownloadButtons
                    comparedReportId={comparedAnalyseId}
                    excelDownloadDisabled={excelDownloadDisabled}
                    hasExcelAccess={hasExcelAccess}
                    isExcelLoading={isExcelLoading}
                    isTrial={isTrial}
                    pdfDownloadDisabled={pdfDownloadDisabled}
                    reportId={analyseId || ''}
                    setIsExcelLoading={setIsExcelLoading}
                    wasLocationSaved={locationSaved}
                  />
                </DropdownContentWrapper>
              )}
              header={(
                <StyledDropdownHeader $disabled={pdfDownloadDisabled}>
                  {isExcelLoading
                    ? <MiniLoader $size='1rem' />
                    : (
                      <>
                        <Download />
                        <StyledBlueSpan>{t('generic.download')}</StyledBlueSpan>
                      </>
                    )}
                </StyledDropdownHeader>
              )}
              noBorder
              transparent
            />
            {(!analysisDbInfo?.visible)
              ? dropdownContent.length
                ? (
                  <DropdownButton
                    disabled={!canBeSave || !checkIfAnyTileSave()}
                    dropdownContent={dropdownContent}
                    mainButton={(
                      <SaveAnalyseModal
                        analyseId={analyseId || ''}
                        handleDelete={handleDelete}
                        trigger={<DropdownMainButtonWrapper data-cy={dataAttributes.analysePage.header.saveAnalyseButton}>{t('generic.save_analyse')}</DropdownMainButtonWrapper>}
                        wasLocationSaved={locationSaved}
                      />
                    )}
                  />
                )
                : (
                  <SaveAnalyseModal
                    analyseId={analyseId || ''}
                    handleDelete={handleDelete}
                    trigger={(
                      <Button
                        data-cy={dataAttributes.analysePage.header.saveAnalyseButton}
                        disabled={!canBeSave || !checkIfAnyTileSave()}
                      >
                        {t('generic.save_analyse')}
                      </Button>
                    )}
                    wasLocationSaved={locationSaved}
                  />
                )
              : null}
            {isEdit && (
              <Button
                disabled={synchronousTilesLoading}
                onClick={() => {
                  const isProjectSaved = analysisDbInfo?.visible
                  exitFromAnalyseAction(dispatch, isEdit)
                  redirectBasedOnProjectState(isProjectSaved)
                }}
              >
                {t('generic.finish_editing')}

              </Button>
            )}
            {!canBeSave && (
              <Button
                disabled={synchronousTilesLoading}
                onClick={() => {
                  window.localStorage.setItem('isEdit', 'true')
                  dispatch(toggleIsEdit(true))
                }}
              >
                {t('generic.edit')}
              </Button>
            )}
            <LanguageSelector
              noArrow
              noBorder
              transparent
            />
          </ActionsWrapper>
        </Wrapper>
      </NoPrint>

    </>
  )
}
